import axios from "axios";
import { useEffect, useState } from "react";

const IMAGE_BASE_URL = "https://1zw31qczi7.execute-api.eu-central-1.amazonaws.com/prod/";

// ── LIFO görsel indirme kuyruğu ────────────────────────────────────────
// En son görünür olan istek en önce yüklenir; aynı anda en fazla
// MAX_CONCURRENT istek uçar. Görünürlükten çıkan (henüz başlamamış) istekler
// kuyruktan düşürülür — boşa indirme yapılmaz.
const MAX_CONCURRENT = 6;
let inFlight = 0;
const fetchQueue = []; // { key, run }
const pendingKeys = new Set();

const scheduleQueue = () => {
  while (inFlight < MAX_CONCURRENT && fetchQueue.length > 0) {
    const entry = fetchQueue.shift();
    if (!entry) break;
    inFlight += 1;
    entry.run().catch(() => null).finally(() => {
      inFlight -= 1;
      scheduleQueue();
    });
  }
};

const enqueueFetch = (entry) => {
  pendingKeys.add(entry.key);
  fetchQueue.unshift(entry); // LIFO
  scheduleQueue();
};

const dequeueFetch = (entry) => {
  const index = fetchQueue.indexOf(entry);
  if (index !== -1) {
    fetchQueue.splice(index, 1);
    pendingKeys.delete(entry.key);
  }
};

const useGetImages = (picture, firmaId, categoryId, options = {}) => {
  const { enabled = true } = options;
  const [image, setImage] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // enabled=false → örn. ürünün PATH'i "x" değil; boş yere resim API'sine istek atma.
    if (!enabled) {
      setIsLoading(false);
      return;
    }
    if (!picture || !firmaId || !categoryId) {
      setIsLoading(false);
      return;
    }

    const cacheKey = `img_${picture}_${firmaId}_${categoryId}`;

    // Session storage'dan kontrol et
    const cachedImage = sessionStorage.getItem(cacheKey);
    if (cachedImage && cachedImage.startsWith("data:image")) {
      setImage(cachedImage);
      setIsLoading(false);
      return;
    }

    if (pendingKeys.has(cacheKey)) return;

    let isActive = true;

    const run = async () => {
      try {
        if (isActive) setIsLoading(true);
        const response = await axios.get(
          `${IMAGE_BASE_URL}${picture}/${firmaId}/${categoryId}`
        );

        // Resim yoksa API data URI yerine XML hata metni döner; onu resim sayma.
        const raw = response?.data;
        const imageData =
          typeof raw === "string" && raw.startsWith("data:image") ? raw : null;
        if (isActive) setImage(imageData);

        // Session storage'a kaydet
        if (imageData) {
          try {
            sessionStorage.setItem(cacheKey, imageData);
          } catch (storageError) {
            // Storage doluysa eski cache'leri temizle
            if (storageError.name === "QuotaExceededError") {
              const keys = Object.keys(sessionStorage);
              const imageKeys = keys.filter((key) => key.startsWith("img_"));
              imageKeys.slice(0, Math.floor(imageKeys.length / 2)).forEach((key) => {
                sessionStorage.removeItem(key);
              });
              try {
                sessionStorage.setItem(cacheKey, imageData);
              } catch (e) {
                console.warn("Session storage kaydedilemedi:", e);
              }
            }
          }
        }
      } catch (err) {
        if (isActive) setError(err.response);
      } finally {
        pendingKeys.delete(cacheKey);
        if (isActive) setIsLoading(false);
      }
    };

    const queueEntry = { key: cacheKey, run };
    enqueueFetch(queueEntry);

    return () => {
      isActive = false;
      dequeueFetch(queueEntry);
    };
  }, [picture, firmaId, categoryId, enabled]);

  return { image, isLoading, error };
};

export default useGetImages;
